import { useSankeyFormContext } from 'contexts/sankeyFormContext';
import Icon from 'ui/Icon';
import { Flex } from 'ui/Layout';
import Tooltip from 'ui/Tooltip';
import { Text } from 'ui/Typography';
import { useDiagramCache } from 'utils/caching';
import { useCurrentRouteSankeyId } from 'utils/routes';

import { flickerAnimation, spinAnimation } from '../../../constants';
import { useCurrentUser } from '../../../utils/user';
import SaveButton from './SaveButton';
import { useTimeAgo } from './utils';

const SaveInfo: React.FC = () => {
  const { user } = useCurrentUser();
  const sankeyId = useCurrentRouteSankeyId();
  const sankeyCache = useDiagramCache(sankeyId);
  const { isSaving, lastSaveTimestamp, lastSaveError } = useSankeyFormContext();

  const timeAgo = useTimeAgo(lastSaveTimestamp);

  // Show the save button if the user can't update Sankeys and there's been
  // an update to the diagram resulting in a cached version.
  if (!user?.can_update_sankeys && sankeyCache.exists()) {
    return <SaveButton />;
  }

  if (isSaving) {
    return (
      <Icon
        animation={`${spinAnimation} 1s infinite linear`}
        color="gray.500"
        fontSize="sm"
        icon="arrows-rotate"
      />
    );
  }

  if (lastSaveError) {
    return (
      <Tooltip showArrow content={<Text>{lastSaveError}</Text>}>
        <Flex
          alignItems="center"
          animation={`${flickerAnimation} 1s infinite`}
          color="red.600"
          cursor="help"
          gap={1}
          pl={2}
        >
          <Icon fontSize="lg" icon="triangle-exclamation" />
          <Text fontSize="sm" fontWeight={600} whiteSpace="nowrap">
            Save error
          </Text>
        </Flex>
      </Tooltip>
    );
  }

  if (lastSaveTimestamp) {
    return (
      <Tooltip
        showArrow
        content={
          <Text lineHeight={2}>
            Your diagram has been saved to the cloud.
            <br />
            Last edit: <b>{timeAgo}</b>
          </Text>
        }
      >
        <Icon color="gray.400" cursor="help" icon="cloud-saved" />
      </Tooltip>
    );
  }

  return null;
};

export default SaveInfo;
